import { useSafeContext } from '@aspendental/shared-utils/react';
import { createContext, useMemo } from 'react';

import { default as baseConfig } from '@shared-components-web/theme/BaseBrandThemeConfig';
import { BrandThemeConfig, TAGBrand } from '@shared-components-web/types';
import { httpClient } from '@shared-components-web/utils/httpClient';

export interface IAppContext {
	theme: TAGBrand;
	environment: EnvironmentType;
	config: BrandThemeConfig;
	appName: string;
	appVersion: string;
	dentrinoClientId?: string;
	dentrinoClientSecret?: string;
	stateCodeFromHeaders?: string;
}
export interface IAppContextProvider extends IAppContext {
	children: React.ReactNode;
}

const AppContext = createContext<IAppContext>({
	theme: 'AspenDental' as TAGBrand,
	environment: 'prod' as EnvironmentType,
	config: baseConfig,
	appName: 'tag_unknown',
	appVersion: 'unknown',
	dentrinoClientId: 'unknown',
	dentrinoClientSecret: 'unknown',
	stateCodeFromHeaders: '',
});
AppContext.displayName = 'AppContext';

type EnvironmentType = 'local' | 'dev' | 'stage' | 'prod';

export function AppContextProvider({
	theme,
	environment,
	config,
	appName,
	appVersion,
	children,
	dentrinoClientId,
	dentrinoClientSecret,
	stateCodeFromHeaders,
}: Readonly<IAppContextProvider>) {
	const resolvedConfig = config ?? baseConfig;

	const appContext = useMemo(
		() => ({
			theme: theme,
			environment: environment,
			config: resolvedConfig,
			appName: appName,
			appVersion: appVersion,
			dentrinoClientId: dentrinoClientId ?? '',
			dentrinoClientSecret: dentrinoClientSecret ?? '',
			stateCodeFromHeaders: stateCodeFromHeaders ?? '',
		}),
		[
			theme,
			environment,
			resolvedConfig,
			appName,
			appVersion,
			dentrinoClientId,
			dentrinoClientSecret,
			stateCodeFromHeaders,
		]
	);

	httpClient.getHTTPClient().defaults.baseURL =
		environment !== 'prod' ? resolvedConfig.siteURLStage : resolvedConfig.siteURL;

	return <AppContext.Provider value={appContext}>{children}</AppContext.Provider>;
}

export function useAppContext() {
	return useSafeContext(AppContext);
}
